import React, { useState, useMemo, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useWishlist } from '../../../context/WishlistContext';

// Best-effort color-name → hex mapping for the swatch picker. Unknown names
// fall back to a 2-letter abbreviation chip rendered inside an empty circle
// (still selectable, just no fill).
const COLOR_MAP = {
  white: '#ffffff', ivory: '#fffff0', cream: '#fef6e4', beige: '#d6c4a4',
  yellow: '#eab308', gold: '#d4af37', mustard: '#e6a900',
  orange: '#f97316', coral: '#ff7f50', peach: '#ffd9b3',
  red: '#dc2626', 'wine red': '#7c1d2c', burgundy: '#7c1d2c',
  pink: '#ec4899', 'rose red': '#e11d48', 'hot pink': '#ff1493',
  purple: '#9333ea', violet: '#8a2be2', lavender: '#b794f4',
  blue: '#3b82f6', 'navy blue': '#1e3a8a', navy: '#1e3a8a',
  'sky blue': '#7dd3fc', 'light blue': '#7dd3fc', teal: '#14b8a6',
  green: '#16a34a', 'army green': '#4b5320', olive: '#808000', mint: '#a7f3d0',
  black: '#000000', gray: '#6b7280', grey: '#6b7280',
  silver: '#c0c0c0', brown: '#92400e', khaki: '#c3b091',
};
function colorNameToHex(name) {
  if (!name) return null;
  return COLOR_MAP[name.toLowerCase().trim()] || null;
}

const ProductInfo = ({ product = {}, variations = [], onVariationImageChange, onAddToCart, onAddToWishlist }) => {
  const wishlist = useWishlist();
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [selectedAttrs, setSelectedAttrs] = useState({});  // { color: 'White', size: 'M' }
  const [quantity, setQuantity] = useState(1);

  // Use the rich variations passed from the parent (with image+price+stock)
  // when available; fall back to the lightweight ones embedded on the product.
  const richVariations = variations.length ? variations : (product?.variations || []);

  // Find matching variation when all attributes are picked.
  const matchedVariation = useMemo(() => {
    const attrs = product?.attributes || [];
    if (!attrs.length || !richVariations.length) return null;
    const allPicked = attrs.every((a) => !!selectedAttrs[a.slug]);
    if (!allPicked) return null;
    return richVariations.find((v) =>
      attrs.every((a) => (v.attributes?.[a.slug] || '').toLowerCase() === (selectedAttrs[a.slug] || '').toLowerCase())
    ) || null;
  }, [product?.attributes, richVariations, selectedAttrs]);

  // When the shopper picks a value for a placeholder-styled attribute, the
  // parent gallery should jump to that variation's image. Fire onVariationImageChange
  // whenever any single attribute's first-matching variation image is known.
  useEffect(() => {
    if (!onVariationImageChange) return;
    // If we have a full match, prefer its image.
    if (matchedVariation?.image) {
      onVariationImageChange(matchedVariation.image);
      return;
    }
    // Otherwise: for any selected attribute, find the first variation with
    // that value and use its image. Prioritises placeholder-styled attributes
    // (where the image IS the differentiator) over Color/Size which have
    // visual representations of their own.
    const attrs = product?.attributes || [];
    const placeholderRe = /^(element|variant|option|choice|item|product|sample|test)\s*\d+$/i;
    for (const a of attrs) {
      const term = selectedAttrs[a.slug];
      if (!term) continue;
      const isPlaceholderTerm = placeholderRe.test(term.trim());
      const isColorish = /color|colour/i.test(a.name);
      if (!isPlaceholderTerm && !isColorish) continue;
      const v = richVariations.find((vv) =>
        (vv.attributes?.[a.slug] || '').toLowerCase() === term.toLowerCase()
      );
      if (v?.image) {
        onVariationImageChange(v.image);
        return;
      }
    }
  }, [matchedVariation, selectedAttrs, richVariations, product?.attributes, onVariationImageChange]);

  // Auto-pick the first available value for each attribute on first load so
  // shoppers see a default selection (and the matchedVariation resolves).
  useEffect(() => {
    const attrs = product?.attributes || [];
    if (!attrs.length) return;
    setSelectedAttrs((prev) => {
      const next = { ...prev };
      let changed = false;
      attrs.forEach((a) => {
        if (!next[a.slug] && a.terms?.length) {
          next[a.slug] = a.terms[0];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [product?.id, product?.attributes]);
  const isWishlisted = wishlist.has(product?.id);

  const defaultProduct = {};

  const productData = { ...defaultProduct, ...product };
  const currentVariant = productData?.variants?.[selectedVariant];
  // Simple products (no variants) leave currentVariant undefined; fall back
  // to productData.price so the price block actually renders.
  const displayPrice = (typeof currentVariant?.price === 'number')
    ? currentVariant.price
    : (typeof productData?.price === 'number' ? productData.price : 0);
  const discount = productData?.originalPrice ? 
    Math.round(((productData?.originalPrice - productData?.price) / productData?.originalPrice) * 100) : 0;

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart({
        ...productData,
        variant: currentVariant,
        // For variable products: pass the matched variation id + the chosen
        // attribute values so cart + checkout can persist the right SKU.
        variationId: matchedVariation?.id || null,
        selectedAttributes: selectedAttrs,
        quantity
      });
    }
  };

  const handleWishlistToggle = () => {
    wishlist.toggle(productData);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="StarHalf" size={16} className="text-warning fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={16} className="text-muted-foreground" />
      );
    }

    return stars;
  };

  return (
    <div className="space-y-6">
      {/* Brand and Title */}
      <div>
        <p className="text-sm font-medium text-primary mb-2">{productData?.brand}</p>
        <h1 className="text-3xl font-heading font-semibold text-foreground leading-tight">
          {productData?.name}
        </h1>
      </div>
      {productData?.shortDescription && (
        <p className="text-base text-muted-foreground leading-relaxed">
          {productData.shortDescription}
        </p>
      )}
      {/* Rating and Reviews */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-1">
          {renderStars(productData?.rating)}
        </div>
        <span className="text-sm font-medium text-foreground">{productData?.rating}</span>
        <span className="text-sm text-muted-foreground">
          ({productData?.reviewCount?.toLocaleString()} reviews)
        </span>
      </div>
      {/* Pricing */}
      <div className="flex items-center space-x-3">
        <span className="text-3xl font-bold text-foreground">
          ${displayPrice.toFixed(2)}
        </span>
        {productData?.originalPrice && (
          <>
            <span className="text-xl text-muted-foreground line-through">
              ${productData?.originalPrice?.toFixed(2)}
            </span>
            <span className="bg-error text-error-foreground text-sm font-medium px-2 py-1 rounded-lg">
              {discount}% OFF
            </span>
          </>
        )}
      </div>
      {/* Stock Status */}
      <div className="flex items-center space-x-2">
        <Icon 
          name={productData?.inStock ? "CheckCircle" : "XCircle"} 
          size={16} 
          className={productData?.inStock ? "text-success" : "text-error"} 
        />
        <span className={`text-sm font-medium ${
          productData?.inStock ? "text-success" : "text-error"
        }`}>
          {productData?.inStock 
            ? `In Stock (${productData?.stockCount} available)` 
            : 'Out of Stock'
          }
        </span>
      </div>
      {/* Variant Selection — per-attribute picker (Color swatches + Size buttons) */}
      {(productData?.attributes || []).length > 0 && (
        <div className="space-y-4">
          {productData.attributes.map((attr) => {
            const isColorAttr = /color|colour/i.test(attr.name);
            return (
              <div key={attr.slug}>
                <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                  <span>{attr.name}:</span>
                  <span className="text-muted-foreground font-normal">{selectedAttrs[attr.slug] || ''}</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {attr.terms.map((term) => {
                    const isSelected = (selectedAttrs[attr.slug] || '').toLowerCase() === term.toLowerCase();
                    // If this term has a corresponding variation image AND the
                    // term itself looks like a placeholder name (Element1 etc.),
                    // render as an image swatch — the visual IS the meaningful
                    // differentiator, not the term text.
                    const placeholderRe = /^(element|variant|option|choice|item|product|sample|test)\s*\d+$/i;
                    const isPlaceholderTerm = placeholderRe.test(term.trim());
                    if (isPlaceholderTerm) {
                      const v = richVariations.find((vv) =>
                        (vv.attributes?.[attr.slug] || '').toLowerCase() === term.toLowerCase()
                      );
                      if (v?.image) {
                        return (
                          <button
                            key={term}
                            onClick={() => setSelectedAttrs((prev) => ({ ...prev, [attr.slug]: term }))}
                            title={term}
                            aria-label={term}
                            className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                              isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-muted-foreground'
                            }`}
                          >
                            <img src={v.image} alt={term} className="w-full h-full object-cover" />
                          </button>
                        );
                      }
                    }
                    if (isColorAttr) {
                      const hex = colorNameToHex(term);
                      return (
                        <button
                          key={term}
                          onClick={() => setSelectedAttrs((prev) => ({ ...prev, [attr.slug]: term }))}
                          title={term}
                          aria-label={term}
                          className={`relative w-9 h-9 rounded-full border-2 transition-all ${
                            isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-muted-foreground'
                          }`}
                          style={{ backgroundColor: hex }}
                        >
                          {!hex && (
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-foreground">
                              {term.slice(0, 2).toUpperCase()}
                            </span>
                          )}
                        </button>
                      );
                    }
                    return (
                      <button
                        key={term}
                        onClick={() => setSelectedAttrs((prev) => ({ ...prev, [attr.slug]: term }))}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-foreground hover:border-primary'
                        }`}
                      >
                        {term}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Quantity and Actions */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Quantity
          </label>
          <div className="flex items-center space-x-3">
            <div className="flex items-center border border-border rounded-lg">
              <button
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
                className="p-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="Minus" size={16} />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-foreground min-w-[3rem] text-center">
                {quantity}
              </span>
              <button
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= 10}
                className="p-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="Plus" size={16} />
              </button>
            </div>
            <span className="text-sm text-muted-foreground">
              Maximum 10 per order
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            onClick={handleAddToCart}
            disabled={!productData?.inStock}
            iconName="ShoppingCart"
            iconPosition="left"
            className="flex-1"
          >
            Add to Cart
          </Button>
          <Button
            variant="outline"
            onClick={handleWishlistToggle}
            iconName={isWishlisted ? "Heart" : "Heart"}
            className={isWishlisted ? "text-error border-error" : ""}
          >
            {isWishlisted ? 'Saved' : 'Save'}
          </Button>
        </div>
      </div>
      {/* Product Features */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">
          Product Features
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {productData?.features?.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Icon name="Check" size={16} className="text-success flex-shrink-0" />
              <span className="text-sm text-foreground">{feature}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Info */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Truck" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-accent-foreground">
              Free shipping on orders over $50
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Estimated delivery: 3-5 business days
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;